import * as THREE from 'three';

import { FacetSpaces, FacetTypes } from "../../../facets/Facets";
import { HUD_LAYER, HudLayer } from '../HudLayer';
import { SpaceLabel } from './SpaceLabel';
import { guessPlacement } from './SpacesPlacement';
import { flyToObliqueViewOfBox } from "../../../../tools/FitToViewUtil";
import { Label3D } from "../../../../gui/Label3D";
import * as et from "../../../../application/EventTypes";

// Maximum number of labels to update per frame
const BATCH_UPDATE_SIZE = 50;

/** Layer of see through labels, made to be seen top down, with a computed position */
export class SpacesLayer extends HudLayer {
  #cachedPlaceByEid;
  #labelByEid;
  #models;
  #spaces;
  #isClustered;
  #isUpdatingSpacesFacet = false;
  #waitingToLoad = new Map();
  #boundOnFragmentLoaded;

  // Batched update cursors
  #updateCursor;

  constructor(facility, hud, viewer) {
    super(HUD_LAYER.SPACES.id, facility, hud, viewer);

    this.#cachedPlaceByEid = {};
    this.#labelByEid = new Map();
    this.#models = [];
    this.#spaces = [];
    this.#updateCursor = 0;

    this.#boundOnFragmentLoaded = this.#onFragmentLoaded.bind(this);
  }

  clear() {
    for (let label of this.#labelByEid.values()) {
      if (label) {
        // Optional label here as we cache failure to create labels.
        label.dispose();
      }
    }
    this.#labelByEid = new Map();
    this.#cachedPlaceByEid = {};
    this.#updateCursor = 0;
  }

  dispose() {
    this.clear();
  }

  init() {
    return this;
  }

  onCameraChanged(camera) {
    this.invalidate();
  }

  onClick(event, space) {
    const ext = this.viewer.getExtension('Autodesk.Edit2D');
    if (ext?.isCreatingModeOn(this.viewer.toolController.getActiveToolName())) {
      return;
    }

    if (!space) {
      return;
    }

    this.#isUpdatingSpacesFacet = true;

    const spacesFacet = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);

    if (event.shiftKey) {
      this.facility.facetsManager.clearFilter(FacetTypes.spaces, true);
    }
    this.facility.facetsManager.setVisibilityById(FacetTypes.spaces, space.spaceFacetId, true);

    const filter = spacesFacet.filter;
    for (const label of this.#labelByEid.values()) {
      if (label) {
        const shouldSelect = filter.has(label.space.spaceFacetId);
        label.setSelected(shouldSelect, !shouldSelect && filter.size > 0);
      }
    }

    this.#isUpdatingSpacesFacet = false;

    event.preventDefault();
    event.stopPropagation();
  }

  onHiddenChanged( /*hidden*/) {
    this.invalidate();
  }

  onFacetsUpdated(event) {

    if (this.#isUpdatingSpacesFacet) {
      return;
    }

    const spacesFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);
    const levelsFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.levels);

    if (!spacesFacetDef || !levelsFacetDef) {
      this.#setFromSpaces({});
      return;
    }

    let floorFilter = levelsFacetDef.filter;
    if (!floorFilter.size && !spacesFacetDef.filter.size) {
      this.#setFromSpaces({});
      return;
    }

    this.#models.length = 0;
    const doneModels = new Set();
    const spacesByEid = {};

    //Get the list of spaces by listing them from the currently visible Space facet nodes
    const spaceNodes = this.facility.facetsManager.getFacet(FacetTypes.spaces);
    for (let node of spaceNodes) {

      if (node.isUnassignedId) {
        continue;
      }

      const isInFilter = spacesFacetDef.filter.has(node.id);
      if (!floorFilter.size && !isInFilter) {
        continue;
      }

      const model = node.roomSrcModel;

      //In case the space is a physical room (as opposed to some arbitrary grouping in a NWD plant model)
      //make sure the room matches the floor filter too. In general more spaces can be in the facet than
      //belong to a specific filtered floor, because elements assigned to a room can span multiple floors,
      //which will make all rooms that have that element appear in the list.
      if (!isInFilter) {
        const dbId2levelId = model.getData().dbId2levelId;
        const levelDbId = dbId2levelId[node.dbId];
        if (levelDbId > 0) {
          let rmLevelName = model.getInstanceTree().getNodeName(levelDbId, false);
          if (!floorFilter.has(rmLevelName)) {
            continue;
          }
        }
      }

      let idIdx = node.id.lastIndexOf(":");
      if (idIdx === -1 || node.isUnassignedId) {
        continue;
      }

      let roomInfo = {
        externalId: node.id.slice(idIdx + 1),
        dbId: node.dbId,
        name: node.label,
        model
      };

      if (!doneModels.has(model.id)) {
        doneModels.add(model.id);
        this.#models.push(model);
      }
      spacesByEid[roomInfo.externalId] = roomInfo;
    }

    this.#setFromSpaces(spacesByEid);
  }


  onModelChanged({ change }) {
    const { modelId, dbIds, needModelContentsReload } = change;

    if (needModelContentsReload) {
      return;
    }

    const changed = new Set(dbIds);
    for (const space of this.#spaces) {
      const { dbId, externalId, model } = space;
      if (model.urn() === modelId && changed.has(dbId)) {
        // Room name is the only relevant property that could change for the moment.
        this.#labelByEid.get(externalId)?.updateRoomName();
      }
    }
  }

  onClusterTransition(clustered) {
    // will skip ray intersection if clustered
    this.#isClustered = clustered;

    // Apply animation transforms
    for (const label in this.#labelByEid.values()) {
      label?.updateWorldBox();
      label?.updateLabelPosition();
    }
  }

  setVisible(visible) {

    super.setVisible(visible);

    //Hide or show room boundaries together with labels
    this.facility.facetsManager.facetsEffects?.setFloorplanEnabled(visible);

    this.invalidate();

    this.facility.facetsManager.highlightLocations();
    this.viewer.impl.invalidate(true);
  }

  onMouseOver(event, space) {
    if (!event.buttons) {
      this.facility.facetsManager.facetsEffects.addSpaceHighlight(space);
    }

    event.preventDefault();
    event.stopPropagation();
  }

  onMouseOut(event) {
    this.facility.facetsManager.facetsEffects.clearFacetHighlight();

    event.preventDefault();
    event.stopPropagation();
  }

  onDblClick(event, space) {
    //Double-click on one space will fit all currently filtered spaces.
    //This is a subjective choice of what would be more useful, it's easy
    //enough to change this to work on just the space that is target of the double-click.
    let box = new THREE.Box3();
    let box2 = new THREE.Box3();
    for (const roomInfo of this.#spaces) {
      const label = this.#labelByEid.get(roomInfo.externalId);
      if (label?.isSelected) {
        roomInfo.model.getElementBounds(roomInfo.dbId, box2);
        box.union(box2);
      }
    }

    flyToObliqueViewOfBox(this.facility.viewer, box, 1.0);

    event.preventDefault();
    event.stopPropagation();
  }

  invalidate() {
    super.invalidate();
    this.#updateCursor = 0;
  }

  #connectListeners(label, space) {
    let et = label.label;
    et.addEventListener(Label3D.Events.CLICK, ({ event }) => this.onClick(event, space));
    et.addEventListener(Label3D.Events.ON_MOUSE_OVER, ({ event }) => this.onMouseOver(event, space));
    et.addEventListener(Label3D.Events.ON_MOUSE_OUT, ({ event }) => this.onMouseOut(event, space));
    et.addEventListener(Label3D.Events.DBL_CLICK, ({ event }) => this.onDblClick(event, space));
  }

  defineLabelVisibility(space, label, drawRect) {

    if (label.isSelected) {
      return true;
    }

    let shouldBeVisible = label.hasEnoughSpace(drawRect);

    if (shouldBeVisible && !label.isInDOM()) {

      const needListeners = !label.label;
      label.setVisible(true); // Create DOM label for accurate dimensions

      if (needListeners) {
        this.#connectListeners(label, space);
      }

      shouldBeVisible = label.hasEnoughSpace(drawRect);
    }

    // Don't show label if it creates overlap with previously shown labels
    if (shouldBeVisible) {
      label.updateViewBox();

      for (let j = 0; j < this.#updateCursor; j++) {
        const prevSpace = this.#spaces[j];
        const prevLabel = this.#labelByEid.get(prevSpace.externalId);

        if (!prevLabel?.isVisible()) continue;

        // TODO: Simplify labels intersection by reusing LevelLabelPlacer
        if (label.viewBox.intersectsBox(prevLabel.viewBox)) {
          shouldBeVisible = false;
          break;
        }
      }
    }

    return shouldBeVisible;
  }

  update(ts) {
    if (!this.#hasAnyCachedPlacement()) {
      this.isDirty = true; // Keep dirty while loading.
      return false;
    }

    // Check if the room is in an odd angle wrt to the camera, in which we want no label.
    const isLayerVisible = this.isVisible() && !this.#isClustered;
    const drawRect = this.viewer.canvasWrap.getBoundingClientRect();
    const labelsOn = isLayerVisible;

    let hasChanged = false;

    const spacesFacet = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);

    // Update everything if it's quick (all off)
    const batchSize = labelsOn ? BATCH_UPDATE_SIZE : this.#spaces.length;

    for (let i = 0; i < batchSize; i++, this.#updateCursor++) {

      // Stop and reset cursor once all labels have been updated
      if (this.#updateCursor === this.#spaces.length) {
        this.#updateCursor = 0;
        break;
      }

      const space = this.#spaces[this.#updateCursor];
      const label = this.#getOrMakeLabel(space);

      if (label) {
        const shouldBeSelected = spacesFacet.filter.has(label.space.spaceFacetId);

        const oldIsVisible = label.isVisible();
        const shouldBeVisible = labelsOn && this.defineLabelVisibility(space, label, drawRect);

        if (shouldBeVisible) {
          label.refreshMaxWidth();
        }

        if (label.isVisible() !== shouldBeVisible) {
          const needListeners = !label.label;
          label.setVisible(shouldBeVisible);

          if (needListeners) {
            this.#connectListeners(label, space);
          }
        }

        label.setSelected(shouldBeSelected, spacesFacet.filter.size > 0 && !shouldBeSelected);

        hasChanged ||= shouldBeVisible !== oldIsVisible;
      }
    }

    // Layer is no longer dirty if cursor is reset
    this.isDirty = this.#updateCursor !== 0;
    return hasChanged;
  }

  #hasAnyCachedPlacement() {
    for (let eid in this.#cachedPlaceByEid) return true;
    return false;
  }

  #getOrMakeLabel(space) {
    let label = this.#labelByEid.get(space.externalId);
    if (label) {
      return label;
    }

    const placement = this.#cachedPlaceByEid[space.externalId];
    if (space && placement?.position) {
      label = new SpaceLabel(space, placement, this.hud, this);
    } else {
      // console.debug('unable to create a label for incorrect configuration', space, placement);
      label = null;
    }

    this.#labelByEid.set(space.externalId, label);
    return label; // Null label means we cache our inability to produce a label.
  }

  #onFragmentLoaded(event) {
    const perModel = this.#waitingToLoad.get(event.model.id);
    if (perModel) {
      const space = perModel.get(event.fragId);
      if (space) {
        //console.log("delayed processing for space", space.name);
        const placement = guessPlacement(this.viewer, space);
        this.#cachedPlaceByEid[space.externalId] = placement;
        this.invalidate();
        perModel.delete(event.fragId);
        if (perModel.size === 0) {
          this.#waitingToLoad.delete(event.model.id);
          if (this.#waitingToLoad.size === 0) {
            this.viewer.removeEventListener(et.FRAGMENT_LOADED_EVENT, this.#boundOnFragmentLoaded);
          }
        }
      }
    }
  }

  /** Initialize from a set of spaces in view */
  #setFromSpaces(spacesByEid) {

    // Set new cancellable handle
    const newSpaces = Object.values(spacesByEid);

    // Only recompute the placement of spaces we haven't computed yet (now that we have an un-animated position)
    const unknowns = newSpaces.filter(({ externalId }) => !(externalId in this.#cachedPlaceByEid));

    for (let space of unknowns) {
      const placement = guessPlacement(this.viewer, space);

      if (placement?.needsToWait) {
        if (this.#waitingToLoad.size === 0) {
          this.viewer.addEventListener(et.FRAGMENT_LOADED_EVENT, this.#boundOnFragmentLoaded);
        }
        let perModel = this.#waitingToLoad.get(space.model.id);
        if (!perModel) {
          perModel = new Map();
          this.#waitingToLoad.set(space.model.id, perModel);
        }
        for (const fragId of placement.fragIds) {
          perModel.set(fragId, space);
        }
      } else {
        this.#cachedPlaceByEid[space.externalId] = placement;
      }
    }

    // Hide outdated labels.
    for (const old of this.#spaces) {
      if (!spacesByEid[old.externalId]) {
        this.#labelByEid.get(old.externalId)?.setVisible(false);
      }
    }
    this.#spaces = [];

    const tmpBox = new THREE.Box3();
    for (const s of newSpaces) {
      s.model.getElementBounds(s.dbId, tmpBox);

      const spaceExt = {
        ...s, // Avoid polluting model.room with space sorting heuristic
        spaceFacetId: FacetSpaces.makeId(s),
        sizeEstimate: tmpBox.min.distanceToSquared(tmpBox.max)
      };

      this.#spaces.push(spaceExt);
    }

    this.#spaces.sort(byVisibilityPriority);

    this.invalidate();
  }
}

// From Non-MEP to MEP, then from larger to smaller spaces
function byVisibilityPriority(a, b) {
  if (a.isMEPSpace !== b.isMEPSpace) {
    return a.isMEPSpace ? 1 : -1;
  }

  return b.sizeEstimate - a.sizeEstimate;
}